import { matchesWords, type ResultOf, sumAmounts } from '@horof/shared';
import { graphql } from './gql';

/** Every member with their unpaid bills (status derived by the database). */
export const PaymentMembersDocument = graphql(`
  query PaymentMembers {
    members(orderBy: [LAST_NAME_ASC, FIRST_NAME_ASC]) {
      nodes {
        id
        fullName
        memberAddress
        meters {
          nodes {
            label
          }
        }
        unpaidBills: bills(filter: { billStatus: { notEqualTo: PAID } }) {
          nodes {
            id
            remainingAmount
            billStatus
          }
        }
      }
    }
  }
`);

type MemberNode = NonNullable<
  NonNullable<
    ResultOf<typeof PaymentMembersDocument>['members']
  >['nodes'][number]
>;

export type MemberRow = {
  id: string;
  name: string;
  address: string;
  meterLabels: string[];
  unpaidCount: number;
  /** Sum of the unpaid bills' remainingAmount, for display. */
  owed: string;
  overdue: boolean;
};

export function toMemberRows(nodes: (MemberNode | null)[]): MemberRow[] {
  return nodes
    .filter((node): node is MemberNode => node !== null)
    .map((node) => {
      const unpaid = node.unpaidBills.nodes.filter((b) => b !== null);
      return {
        id: node.id,
        name: node.fullName ?? '',
        address: node.memberAddress,
        meterLabels: node.meters.nodes.flatMap((m) =>
          m?.label ? [m.label] : [],
        ),
        unpaidCount: unpaid.length,
        owed: sumAmounts(unpaid.map((b) => b.remainingAmount)),
        overdue: unpaid.some((b) => b.billStatus === 'OVERDUE'),
      };
    });
}

export const matchesMember = (member: MemberRow, query: string) =>
  matchesWords([member.name, member.address, ...member.meterLabels], query);
